import { queryOptions, useQuery } from "@tanstack/react-query";

import type { GetResult } from "api";
import { getAgelessCapabilities } from "api/methods";

export type AgelessFlowType = "aesthetic" | "wellness" | "both" | "none";

export interface AgelessCapabilities {
  isEnabled: boolean;
  hasAesthetic: boolean;
  hasWellness: boolean;
  flowUrl: string | null;
  flowType: AgelessFlowType;
  showOnPatientHome: boolean;
  isSubscriptionPaused: boolean;
}

type AgelessCapabilitiesApiResponse = GetResult<"/ageless/capabilities">;

export function resolveFlowType(hasAesthetic: boolean, hasWellness: boolean): AgelessFlowType {
  if (hasAesthetic && hasWellness) return "both";
  if (hasAesthetic) return "aesthetic";
  if (hasWellness) return "wellness";
  return "none";
}

export function mapAgelessCapabilitiesResponse(
  data: AgelessCapabilitiesApiResponse,
): AgelessCapabilities {
  return {
    isEnabled: data.isEnabled,
    hasAesthetic: data.hasAesthetic,
    hasWellness: data.hasWellness,
    flowUrl: data.flowUrl ?? null,
    flowType: resolveFlowType(data.hasAesthetic, data.hasWellness),
    showOnPatientHome: data.showOnPatientHome,
    isSubscriptionPaused: data.isSubscriptionPaused,
  };
}

export function useAgelessCapabilities(): {
  capabilities: AgelessCapabilities | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery(
    queryOptions({
      queryKey: getAgelessCapabilities.key(),
      queryFn: async () => mapAgelessCapabilitiesResponse(await getAgelessCapabilities()),
      staleTime: 1000 * 60 * 5,
    }),
  );

  return {
    capabilities: data ?? null,
    isLoading,
  };
}
